import React, { useState } from 'react';
import { ChevronUp, ChevronDown, AlertTriangle } from 'lucide-react';

export default function BiharDataTable({ athletes, onAthleteClick }) {
  const [sortBy, setSortBy] = useState('student_id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortedAthletes = [...athletes].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (typeof aVal === 'string') {
      return sortOrder === 'asc' 
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const totalPages = Math.ceil(sortedAthletes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAthletes = sortedAthletes.slice(startIndex, startIndex + itemsPerPage);

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '-';
    return Number(num).toFixed(1);
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    // Format as hh:mm:ss:ms for consistency
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 100);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${ms.toString().padStart(2, '0')}`;
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? 
      <ChevronUp className="w-4 h-4" /> : 
      <ChevronDown className="w-4 h-4" />;
  };

  const OutlierCell = ({ athlete, metric, value, unit = '' }) => {
    const outlierFlag = athlete.outlier_flags?.[metric];
    const isOutlier = outlierFlag?.isOutlier;
    const wasNormalized = outlierFlag?.wasNormalized;

    // Determine text color: red for outliers, yellow for normalized, default for normal
    let textColor = 'text-slate-300';
    if (isOutlier && !wasNormalized) {
      textColor = 'text-red-400'; // Out of range but not normalized
    } else if (wasNormalized) {
      textColor = 'text-yellow-400'; // Data was manipulated
    }

    return (
      <td className={`px-4 py-3 text-sm`}>
        <div className="flex items-center space-x-2">
          {(isOutlier || wasNormalized) ? (
            <div className="group relative flex items-center space-x-2">
              <span className={`${textColor} font-medium`}>
                {value} {value !== '-' ? unit : ''}
              </span>
              {wasNormalized && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 min-w-max">
                <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 border border-slate-700 shadow-xl">
                  {wasNormalized && (
                    <div className="mb-1">
                      <span className="text-yellow-400 font-semibold">Data Corrected</span>
                      <div className="mt-1">
                        Original value: <span className="font-mono text-blue-300">
                          {outlierFlag.originalValue !== undefined && outlierFlag.originalValue !== null 
                            ? outlierFlag.originalValue 
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  )}
                  {isOutlier && <div className="text-red-300">{outlierFlag.reason}</div>}
                </div>
              </div>
            </div>
          ) : (
            <span className={textColor}>
              {value} {value !== '-' ? unit : ''}
            </span>
          )}
        </div>
      </td>
    );
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700 overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-900/50 border-b border-slate-700">
            <tr>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('student_id')}
              >
                <div className="flex items-center space-x-1">
                  <span>ID</span>
                  <SortIcon column="student_id" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('full_name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  <SortIcon column="full_name" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('gender_normalized')}
              >
                <div className="flex items-center space-x-1">
                  <span>Gender</span>
                  <SortIcon column="gender_normalized" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('height_cm')}
              >
                <div className="flex items-center space-x-1">
                  <span>Height</span>
                  <SortIcon column="height_cm" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('weight_kg')}
              >
                <div className="flex items-center space-x-1">
                  <span>Weight</span>
                  <SortIcon column="weight_kg" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('vertical_jump_cm')}
              >
                <div className="flex items-center space-x-1">
                  <span>V. Jump</span>
                  <SortIcon column="vertical_jump_cm" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('broad_jump_cm')}
              >
                <div className="flex items-center space-x-1">
                  <span>Broad Jump</span>
                  <SortIcon column="broad_jump_cm" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('sprint_30m_sec')}
              >
                <div className="flex items-center space-x-1">
                  <span>30m Sprint</span>
                  <SortIcon column="sprint_30m_sec" />
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                onClick={() => handleSort('run_800m_sec')}
              >
                <div className="flex items-center space-x-1">
                  <span>800m Run</span>
                  <SortIcon column="run_800m_sec" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {paginatedAthletes.map((athlete, idx) => (
              <tr 
                key={idx}
                onClick={() => onAthleteClick(athlete)}
                className="hover:bg-slate-700/30 cursor-pointer transition-colors"
              >
                <td className="px-4 py-3 text-sm text-slate-400 font-mono">
                  {athlete.student_id || athlete.sl_no || '-'}
                </td>
                <td className="px-4 py-3 text-sm text-white font-medium">
                  {athlete.full_name || `${athlete.first_name || ''} ${athlete.last_name || ''}`.trim() || '-'}
                </td>
                <td className="px-4 py-3 text-sm">
                  {(() => {
                    const g = (athlete.gender_normalized || athlete.gender || '').toString().trim().toLowerCase();
                    if (g === 'male' || g === 'm') return <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">M</span>;
                    if (g === 'female' || g === 'f') return <span className="px-2 py-0.5 bg-pink-500/20 text-pink-400 text-xs font-medium rounded">F</span>;
                    return <span className="text-slate-500 text-xs">-</span>;
                  })()}
                </td>
                <OutlierCell athlete={athlete} metric="height_cm" value={formatNumber(athlete.height_cm)} unit="cm" />
                <OutlierCell athlete={athlete} metric="weight_kg" value={formatNumber(athlete.weight_kg)} unit="kg" />
                <OutlierCell athlete={athlete} metric="vertical_jump_cm" value={formatNumber(athlete.vertical_jump_cm)} unit="cm" />
                <OutlierCell athlete={athlete} metric="broad_jump_cm" value={formatNumber(athlete.broad_jump_cm)} unit="cm" />
                <OutlierCell athlete={athlete} metric="sprint_30m_sec" value={formatNumber(athlete.sprint_30m_sec)} unit="s" />
                <OutlierCell athlete={athlete} metric="run_800m_sec" value={formatTime(athlete.run_800m_sec)} unit="" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bg-slate-900/50 border-t border-slate-700 px-6 py-4 flex items-center justify-between">
        <div className="text-sm text-slate-400">
          Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, athletes.length)} of {athletes.length} athletes
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-slate-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-slate-400">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-slate-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
